import { useEffect, useState } from 'react';
import FrontImage from "../Assets/Images/Front_Image.png";
import "./Home.css";
import Typewriter from "typewriter-effect";
import GoogleReviewSection from "./GoogleReviewSection.js";
import ServiceCard from "./ServiceCard.js";
import Section4 from "./Section4.js";
import LogoSection from "./LogoSection.js";
import HomeFAQs from "./HomeFAQs.js";
import WhatWeDoSection from "./WhatWeDoSection.js";
import ContactForm from "../ContactForm/ContactFormModal.js";

const Home = () => {
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    const createdMeta = [];

    const setMetaTag = ({ attribute, value, content }) => {
      const selector = `meta[${attribute}="${value}"]`;
      let meta = document.head.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
        createdMeta.push(meta);
      }
      meta.setAttribute('content', content);
    };

    const upsertCanonical = (href) => {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
      return link;
    };

    const createJsonLd = (id, data) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
      return script;
    };

    document.title = 'BooksComply | U.S. Bookkeeping, Tax & Company Formation Services in USA';

    setMetaTag({
      attribute: 'name',
      value: 'description',
      content: 'Outsource bookkeeping, tax filing, and company formation for U.S. businesses. Affordable, compliant, and powered by expert accountants in the USA.'
    });
    setMetaTag({
      attribute: 'name',
      value: 'keywords',
      content: 'US bookkeeping outsourcing, US tax filing partner, virtual CFO USA, USA company incorporation support, BooksComply accountants'
    });
    setMetaTag({ attribute: 'name', value: 'robots', content: 'index, follow' });
    setMetaTag({ attribute: 'property', value: 'og:title', content: 'BooksComply | U.S. Bookkeeping, Tax & Company Formation Services in USA' });
    setMetaTag({ attribute: 'property', value: 'og:description', content: 'Outsource bookkeeping, tax filing, and company formation for U.S. businesses. Affordable, compliant, and powered by expert accountants in the USA.' });
    setMetaTag({ attribute: 'property', value: 'og:image', content: 'https://www.bookscomply.com/Assets/Images/Front_Image.png' });
    setMetaTag({ attribute: 'property', value: 'og:url', content: 'https://www.bookscomply.com/' });
    setMetaTag({ attribute: 'name', value: 'twitter:card', content: 'summary_large_image' });

    const canonicalLink = upsertCanonical('https://www.bookscomply.com/');

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'BooksComply',
      url: 'https://www.bookscomply.com',
      logo: 'https://www.bookscomply.com/Assets/Images/BooksCompl.png',
      description: 'BooksComply provides end-to-end US accounting, bookkeeping, tax filing, and incorporation solutions for founders worldwide.',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+91 9428482371',
        contactType: 'customer service',
        areaServed: 'USA'
      },
      sameAs: [
        'https://in.linkedin.com/company/bookscomply',
        'https://x.com/njp_advisors'
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '124'
      }
    };

    const servicesSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'BooksComply USA Expansion Services',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Service',
            '@id': 'https://bookscomply.com/tax-return',
            name: 'Tax Return & Filing Services (USA)',
            description: 'BooksComply prepares, reviews, and files federal and state returns for founders scaling in the United States.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Tax Preparation',
            serviceOutput: 'Filed US federal and state tax returns with compliance support',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/tax-return',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Service',
            '@id': 'https://bookscomply.com/bookkeeping',
            name: 'Bookkeeping for U.S. Businesses',
            description: 'Dedicated bookkeeping pods deliver monthly reconciliations, management reports, and audit-ready ledgers.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Bookkeeping Service',
            serviceOutput: 'GAAP-compliant monthly financial statements',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/bookkeeping',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Service',
            '@id': 'https://bookscomply.com/cfo-advisory',
            name: 'Virtual CFO & Advisory',
            description: 'Scenario planning, board reporting, and cash-flow insights delivered by senior BooksComply finance leaders.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Financial Advisory Service',
            serviceOutput: 'Strategic finance models and investor-ready dashboards',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/cfo-advisory',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Service',
            '@id': 'https://bookscomply.com/usa-company-formation',
            name: 'USA Company Formation',
            description: 'End-to-end LLC and C-Corp formation with EIN registration, registered agent setup, and banking guidance.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Business Incorporation Service',
            serviceOutput: 'Formed US legal entity with compliance roadmap',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/usa-company-formation',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        }
      ]
    };

    const reviewSchema = {
      '@context': 'https://schema.org',
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: 'Chirag Darji'
      },
      itemReviewed: {
        '@type': 'Organization',
        name: 'BooksComply'
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5'
      },
      reviewBody: 'BooksComply handled our Wyoming incorporation, filings, and ongoing compliance with speed and transparency.'
    };

    const organizationScript = createJsonLd('home-organization-schema', organizationSchema);
    const servicesScript = createJsonLd('home-services-schema', servicesSchema);
    const reviewScript = createJsonLd('home-review-schema', reviewSchema);

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Is BooksComply legal for U.S. bookkeeping and tax filing?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Yes. BooksComply's accountants follow IRS-compliant processes and work with CPAs in the U.S."
          }
        },
        {
          '@type': 'Question',
          name: 'How long does it take to form a company in the USA?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most U.S. company formations are completed within 3–5 business days with our team.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can I track my project progress with BooksComply?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. Every client gets secure dashboard access to monitor real-time status, updates, and reports.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can I outsource my U.S. tax returns if I live abroad?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. We help expats and small businesses file taxes remotely with full compliance.'
          }
        }
      ]
    };
    const faqScript = createJsonLd('home-faq-schema', faqSchema);

    return () => {
      createdMeta.forEach((meta) => document.head.removeChild(meta));
      [organizationScript, servicesScript, reviewScript, faqScript].forEach((script) => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      if (canonicalLink && canonicalLink.parentNode) {
        canonicalLink.parentNode.removeChild(canonicalLink);
      }
    };
  }, []);

  const handleCloseContactForm = () => {
    setShowContactForm(false);
  };

  return (
    <main className="home-page">
      <section id="homeSection" className="bg-light pt-5 pb-3">
        <div className="container">
          <div className="row align-items-center">
            {/* Left Column: Taglines */}
            <div className="col-md-6">
              <h1 className="display-3 fw-bolder mb-3">
                <span className="text-black-force">Grow Your U.S. Business with</span>
                <br />
                <span className="typewriter-text text-accent fw-normal font-change font-change1">
                  <Typewriter
                    options={{
                      strings: ["Bookkeeping", "CFO Advisory", "USA Company Formation", "Tax Return"],
                      autoStart: true,
                      loop: true,
                      deleteSpeed: 0.1,
                    }}
                  />
                </span>
              </h1>
              <p className="lead mt-4 text-black-force" style={{ color: '#000000' }}>
                BooksComply helps startups and entrepreneurs simplify company formation in the USA, manage tax filing, and streamline bookkeeping services — all handled by certified professionals.
              </p>
              <div className="d-flex flex-wrap gap-3 mt-4">
                <button
                  className="btn btn-primary-1 rounded-pill px-4 py-2"
                  onClick={() => setShowContactForm(true)}
                  style={{
                    backgroundColor: '#0d6efd',
                    border: 'none',
                    boxShadow: '0 4px 6px rgba(13, 110, 253, 0.2)',
                    transition: 'all 0.3s ease',
                    minWidth: '200px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(13, 110, 253, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 6px rgba(13, 110, 253, 0.2)';
                  }}
                >
                  Book a Free Consultation
                </button>
                <button
                  className="btn btn-outline-primary rounded-pill px-4 py-2"
                  onClick={() => window.location.href = '/pricing'}
                  style={{
                    color: '#0d6efd',
                    borderColor: '#0d6efd',
                    backgroundColor: 'transparent',
                    transition: 'all 0.3s ease',
                    minWidth: '150px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(13, 110, 253, 0.05)';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  View Pricing
                </button>
              </div>
            </div>

            {/* Right Column: Image */}
            <div className="col-md-6 text-center">
              <div className="front-image-wrap">
                <img
                  src={FrontImage}
                  alt="Bookkeeping Services"
                  className="front-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <ServiceCard />
      <WhatWeDoSection />
      <GoogleReviewSection />
      <Section4 />
      <LogoSection />
      <HomeFAQs />

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <ContactForm onClose={handleCloseContactForm} />
          </div>
        </div>
      )}
    </main>
  );
};
export default Home;